import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageCircle, Search, Loader2, Plus, Star, ChevronRight } from 'lucide-react';
import { api } from '@/lib/api';
import { useSocket } from '@/hooks/useSocket';
import { useDevice } from '@/hooks/useDeviceType';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/PageHeader';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDistanceToNow } from 'date-fns';
import { offlineDataCache } from '@/lib/offlineDataCache';
import { OfflineDataBanner } from '@/components/OfflineDataBanner';
import { NewConversationDialog } from '@/components/messages/NewConversationDialog';
import { toast } from 'sonner';
import { cn, displayName } from '@/lib/utils';

// Lazy load desktop version
const MessagesDesktopPage = lazy(() => import('./MessagesPage.desktop'));

function MessagesSkeleton() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

interface Conversation {
  id: string;
  otherUser?: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
    organization?: string;
  };
  lastMessage?: {
    id: string;
    content: string;
    sentAt: string;
    senderId: string;
    isRead: boolean;
  };
  unreadCount: number;
  lastMessageAt: string;
}

export default function MessagesPage() {
  const { isDesktop } = useDevice();

  // Render desktop version for desktop users
  if (isDesktop) {
    return (
      <Suspense fallback={<MessagesSkeleton />}>
        <MessagesDesktopPage />
      </Suspense>
    );
  }

  // Mobile/Tablet version
  return <MessagesMobilePage />;
}

function MessagesMobilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { socket, isConnected } = useSocket();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isNewConversationOpen, setIsNewConversationOpen] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [favoritingIds, setFavoritingIds] = useState<Set<string>>(new Set());

  // Fast path: if we navigated here with a target user id, start/open the conversation and redirect.
  useEffect(() => {
    const state = location.state as any;
    const recipientId: string | undefined = state?.startConversationWithUserId;
    if (!recipientId) return;

    let isCanceled = false;

    (async () => {
      try {
        const res = await api.post<{ success: boolean; data?: { id: string } }>(
          '/messages/conversations',
          { recipientId }
        );

        const conversationId = res?.data?.id;
        if (!conversationId || isCanceled) return;

        navigate(`/messages/${conversationId}`, { replace: true });
      } catch (error) {
        console.error('Failed to start conversation', error);
      }
    })();

    return () => {
      isCanceled = true;
    };
  }, [location.state, navigate]);

  const fetchConversations = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) {
        setIsLoading(true);
      }
      const response = await api.get<{ success: boolean; data: Conversation[] }>('/messages/conversations');
      setConversations(response.data || []);

      void offlineDataCache.set('messages:conversations', response.data || [])
        .catch((e) => console.error('Failed to write conversations cache', e));
    } catch (error) {
      console.error('Failed to fetch conversations', error);
      const cached = await offlineDataCache.get<Conversation[]>('messages:conversations');
      setConversations(cached?.data ?? []);
    } finally {
      setIsLoading(false);
      setHasFetchedOnce(true);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    if (!hasFetchedOnce) {
      fetchConversations(true);
      fetchFavorites();
    }
  }, [fetchConversations, hasFetchedOnce]);

  const fetchFavorites = async () => {
    try {
      const response = await api.get('/users/favorites');
      const data = (response as any).data || [];
      setFavorites(new Set<string>(data.map((f: any) => f.id)));
    } catch (error) {
      console.error('Failed to fetch favorites', error);
    }
  };

  const toggleFavorite = async (userId: string) => {
    if (favoritingIds.has(userId)) return;
    const currentlyFavorited = favorites.has(userId);
    setFavoritingIds(prev => new Set(prev).add(userId));
    try {
      if (currentlyFavorited) {
        await api.delete(`/users/${userId}/favorite`);
        setFavorites(prev => { const next = new Set(prev); next.delete(userId); return next; });
        toast.success('Removed from My Connections');
      } else {
        await api.post(`/users/${userId}/favorite`);
        setFavorites(prev => new Set(prev).add(userId));
        toast.success('Added to My Connections');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || error.message || 'Failed to update connection');
    } finally {
      setFavoritingIds(prev => { const next = new Set(prev); next.delete(userId); return next; });
    }
  };

  // Listen for new message notifications via Socket.IO
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = () => {
      // Refresh conversations when a new message is received
      fetchConversations();
    };

    socket.on('message_notification', handleNewMessage);
    socket.on('new_message', handleNewMessage);

    return () => {
      socket.off('message_notification', handleNewMessage);
      socket.off('new_message', handleNewMessage);
    };
  }, [socket, fetchConversations]);

  // Polling fallback when Socket.IO is not connected (with exponential backoff)
  useEffect(() => {
    if (isConnected) return;

    let pollDelay = 5000; // Start at 5 seconds
    const maxDelay = 30000; // Cap at 30 seconds
    let timeoutId: ReturnType<typeof setTimeout>;

    const poll = async () => {
      await fetchConversations(false);
      // Increase delay with exponential backoff (5s -> 10s -> 20s -> 30s max)
      pollDelay = Math.min(pollDelay * 2, maxDelay);
      timeoutId = setTimeout(poll, pollDelay);
    };

    // Start first poll
    timeoutId = setTimeout(poll, pollDelay);

    return () => clearTimeout(timeoutId);
  }, [isConnected, fetchConversations]);

  const getConversationDisplayName = (user: { firstName: string; lastName: string } | undefined) => {
    if (!user) return 'Unknown User';
    return displayName(user);
  };

  const getConversationInitials = (user: { firstName: string; lastName: string } | undefined) => {
    if (!user) return '?';
    return displayName(user)
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const filteredConversations = conversations.filter(conv =>
    conv.otherUser ? displayName(conv.otherUser).toLowerCase().includes(searchQuery.toLowerCase()) : false
  );

  return (
    <div className="min-h-screen bg-gradient-subtle pb-24">
      <PageHeader
        title="Messages"
        subtitle={`${conversations.length} conversation${conversations.length !== 1 ? 's' : ''}`}
      />

      <main className="px-3 md:px-4 pt-3 md:pt-4 space-y-3 md:space-y-4">
        <OfflineDataBanner />

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-11 md:h-10 text-base"
          />
        </div>

        {/* New Conversation Section */}
        <Card className="p-3 md:p-4 bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20 shadow-md">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-medium">Start a new conversation</p>
            <Button
              onClick={() => setIsNewConversationOpen(true)}
              size="sm"
              className="gap-2 shrink-0 h-11 md:h-10"
            >
              <Plus className="h-4 w-4" />
              Message
            </Button>
          </div>
        </Card>

        {/* Conversations List */}
        <div className="space-y-2 md:space-y-2">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Card key={i} className="p-2.5 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <Skeleton className="h-9 w-9 md:h-12 md:w-12 rounded-full" />
                <div className="flex-1 space-y-1.5 md:space-y-2">
                  <Skeleton className="h-3 md:h-4 w-24 md:w-32" />
                  <Skeleton className="h-2.5 md:h-3 w-36 md:w-48" />
                </div>
              </div>
            </Card>
          ))
        ) : filteredConversations.length === 0 ? (
          <div className="flex items-center justify-center min-h-[50vh]">
            <Card className="p-8 md:p-12 text-center">
              <MessageCircle className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg md:text-xl font-semibold text-muted-foreground">
                {searchQuery ? 'No conversations found' : 'No messages'}
              </h3>
              <p className="text-sm text-muted-foreground mt-2">
                Start a conversation from your connections!
              </p>
            </Card>
          </div>
        ) : (
          filteredConversations.map((conversation) => {
            const isUnread = conversation.unreadCount > 0;

            return (
              <Card
                key={conversation.id}
                className={`p-3 md:p-4 hover:shadow-md transition-all cursor-pointer ${
                  isUnread
                    ? 'bg-accent/5 border-l-4 border-l-accent shadow-sm'
                    : 'border-l-4 border-l-transparent'
                }`}
                onClick={() => navigate(`/messages/${conversation.id}`)}
              >
                <div className="flex items-center gap-3 md:gap-3">
                  <Avatar className={`h-11 w-11 md:h-12 md:w-12 shrink-0 ${isUnread ? 'ring-2 ring-accent/30' : ''}`}>
                    <AvatarImage src={conversation.otherUser?.avatarUrl || undefined} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm md:text-sm">
                      {getConversationInitials(conversation.otherUser)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <h3 className={`${isUnread ? 'font-bold' : 'font-semibold'} text-foreground truncate text-base mb-1`}>
                      {getConversationDisplayName(conversation.otherUser)}
                    </h3>
                    <p className={`text-sm ${isUnread ? 'text-foreground font-medium' : 'text-muted-foreground'} truncate`}>
                      {conversation.lastMessage?.content || 'No messages yet'}
                    </p>
                  </div>

                  <div className="flex flex-col items-center shrink-0">
                    <span className={`text-xs mb-1 ${isUnread ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>
                      {conversation.lastMessage
                        ? formatDistanceToNow(new Date(conversation.lastMessage.sentAt), { addSuffix: false })
                        : ''}
                    </span>
                    <div className="flex items-center gap-1">
                      {conversation.otherUser && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn(
                            'h-8 w-8',
                            favorites.has(conversation.otherUser.id)
                              ? 'text-yellow-500 hover:text-yellow-600'
                              : 'text-muted-foreground hover:text-yellow-500'
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(conversation.otherUser!.id);
                          }}
                          disabled={favoritingIds.has(conversation.otherUser.id)}
                        >
                          {favoritingIds.has(conversation.otherUser.id) ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Star className={cn('h-4 w-4', favorites.has(conversation.otherUser.id) && 'fill-current')} />
                          )}
                        </Button>
                      )}
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      <Badge className={cn('flex-shrink-0 text-xs h-5 px-2 font-semibold', isUnread ? 'bg-accent' : 'invisible')}>
                        {conversation.unreadCount}
                      </Badge>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })
        )}
        </div>
      </main>

      {/* New Conversation Dialog */}
      <NewConversationDialog
        open={isNewConversationOpen}
        onOpenChange={setIsNewConversationOpen}
      />
    </div>
  );
}
